import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  Receipt,
  TrendingUp,
  CreditCard,
  Payment,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { nowCv } from '../../../utils/date';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-CV', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ' CVE';
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const SispBilling: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Billing state
  const now = nowCv(); // mês/ano de CV (ler com getUTC*)
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [billingData, setBillingData] = useState<any>(null);

  // Tariffs state
  const [tariffs, setTariffs] = useState<any[]>([]);
  const [tariffDialog, setTariffDialog] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<any>(null);
  const [tariffForm, setTariffForm] = useState({
    codigo: '', nome: '', descricao: '', tipo_transacao: 'POS_VIRTUAL', tipo_cartao: 'VINTI4',
    tipo_taxa: 'PERCENTAGEM', taxa_percentual: 0, taxa_fixa: 0,
    taxa_sisp_percentual: 0, taxa_sisp_fixa: 0,
    taxa_agente_percentual: 0, taxa_agente_fixa: 0,
    vigencia_inicio: '2025-01-01', vigencia_fim: '',
  });

  const periodo = `${year}-${String(month).padStart(2, '0')}`;

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/settings/sisp-billing?periodo=${periodo}`);
      if (response.data.success) setBillingData(response.data.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  const fetchTariffs = useCallback(async () => {
    try {
      const response = await api.get('/admin/settings/sisp-tariffs');
      if (response.data.success) setTariffs(response.data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);
  useEffect(() => { fetchTariffs(); }, [fetchTariffs]);

  const handleSeedTariffs = async () => {
    try {
      await api.post('/admin/settings/sisp-tariffs/seed');
      fetchTariffs();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleOpenTariff = (tariff?: any) => {
    if (tariff) {
      setSelectedTariff(tariff);
      setTariffForm({
        codigo: tariff.codigo, nome: tariff.nome, descricao: tariff.descricao || '',
        tipo_transacao: tariff.tipo_transacao, tipo_cartao: tariff.tipo_cartao,
        tipo_taxa: tariff.tipo_taxa,
        taxa_percentual: tariff.taxa_percentual, taxa_fixa: tariff.taxa_fixa,
        taxa_sisp_percentual: tariff.taxa_sisp_percentual, taxa_sisp_fixa: tariff.taxa_sisp_fixa,
        taxa_agente_percentual: tariff.taxa_agente_percentual, taxa_agente_fixa: tariff.taxa_agente_fixa,
        vigencia_inicio: tariff.vigencia_inicio?.slice(0, 10) || '2025-01-01',
        vigencia_fim: tariff.vigencia_fim?.slice(0, 10) || '',
      });
    } else {
      setSelectedTariff(null);
      setTariffForm({
        codigo: '', nome: '', descricao: '', tipo_transacao: 'POS_VIRTUAL', tipo_cartao: 'VINTI4',
        tipo_taxa: 'PERCENTAGEM', taxa_percentual: 0, taxa_fixa: 0,
        taxa_sisp_percentual: 0, taxa_sisp_fixa: 0,
        taxa_agente_percentual: 0, taxa_agente_fixa: 0,
        vigencia_inicio: '2025-01-01', vigencia_fim: '',
      });
    }
    setTariffDialog(true);
  };

  const handleSaveTariff = async () => {
    try {
      if (selectedTariff) {
        await api.put(`/admin/settings/sisp-tariffs/${selectedTariff.id}`, tariffForm);
      } else {
        await api.post('/admin/settings/sisp-tariffs', tariffForm);
      }
      setTariffDialog(false);
      fetchTariffs();
      fetchBilling();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteTariff = async (id: string) => {
    if (!window.confirm('Desativar esta tarifa?')) return;
    try {
      await api.delete(`/admin/settings/sisp-tariffs/${id}`);
      fetchTariffs();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const tipoTransacaoLabel: Record<string, string> = {
    RECARGA: 'Recarga',
    PAGAMENTO_SERVICO: 'Pagamento Servico',
    POS_VIRTUAL: 'POS Virtual WEB',
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Faturacao SISP
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Resumo Mensal" icon={<Receipt />} iconPosition="start" />
        <Tab label="Tarifario" icon={<CreditCard />} iconPosition="start" />
      </Tabs>

      {/* TAB 0: Resumo Mensal */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField select size="small" label="Mes" value={month} onChange={(e) => setMonth(Number(e.target.value))} sx={{ width: 160 }}>
            {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Ano" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 120 }}>
            {[2024, 2025, 2026, 2027].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : billingData ? (
          <>
            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Card><CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Transacoes</Typography>
                  <Typography variant="h5" fontWeight={700}>{billingData.totais.transacoes}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Card><CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Volume Total</Typography>
                  <Typography variant="h5" fontWeight={700}>{formatCurrency(billingData.totais.valor)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Card sx={{ bgcolor: 'warning.light' }}><CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">Comissao Total</Typography>
                  <Typography variant="h5" fontWeight={700}>{formatCurrency(billingData.totais.comissao_total)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Card sx={{ bgcolor: 'info.light' }}><CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">SISP</Typography>
                  <Typography variant="h5" fontWeight={700}>{formatCurrency(billingData.totais.comissao_sisp)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Card sx={{ bgcolor: 'success.light' }}><CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">Agente (Pagali)</Typography>
                  <Typography variant="h5" fontWeight={700}>{formatCurrency(billingData.totais.comissao_agente)}</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            {/* Detail Table */}
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>Rubrica</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Cartao</TableCell>
                      <TableCell align="right">Transacoes</TableCell>
                      <TableCell align="right">Volume</TableCell>
                      <TableCell align="center">Taxa</TableCell>
                      <TableCell align="right">Comissao Total</TableCell>
                      <TableCell align="right">SISP</TableCell>
                      <TableCell align="right">Agente</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billingData.linhas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">Sem transacoes neste periodo</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {billingData.linhas.map((l: any, i: number) => (
                          <TableRow key={i} hover>
                            <TableCell>
                              <Chip label={l.rubrica} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{tipoTransacaoLabel[l.tipo_transacao] || l.tipo_transacao}</TableCell>
                            <TableCell>
                              <Chip label={l.tipo_cartao} size="small"
                                color={l.tipo_cartao === 'VINTI4' ? 'success' : l.tipo_cartao === 'VISA' ? 'primary' : 'default'} />
                            </TableCell>
                            <TableCell align="right">{l.total_transacoes}</TableCell>
                            <TableCell align="right">{formatCurrency(parseFloat(l.valor_total))}</TableCell>
                            <TableCell align="center">
                              <Typography variant="caption" fontFamily="monospace">{l.taxa_info}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(l.comissao_total)}</TableCell>
                            <TableCell align="right">{formatCurrency(l.comissao_sisp)}</TableCell>
                            <TableCell align="right">{formatCurrency(l.comissao_agente)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{billingData.totais.transacoes}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(billingData.totais.valor)}</TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(billingData.totais.comissao_total)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(billingData.totais.comissao_sisp)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(billingData.totais.comissao_agente)}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        ) : null}
      </TabPanel>

      {/* TAB 1: Tarifário */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          {tariffs.length === 0 && (
            <Button variant="outlined" onClick={handleSeedTariffs}>Carregar Tarifario 2025</Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenTariff()}>Nova Tarifa</Button>
        </Box>

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Codigo</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo Transacao</TableCell>
                  <TableCell>Cartao</TableCell>
                  <TableCell>Tipo Taxa</TableCell>
                  <TableCell align="right">Taxa Total</TableCell>
                  <TableCell align="right">SISP</TableCell>
                  <TableCell align="right">Agente</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell align="center" width={100}>Accoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tariffs.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell><Chip label={t.codigo} size="small" variant="outlined" /></TableCell>
                    <TableCell>{t.nome}</TableCell>
                    <TableCell>{tipoTransacaoLabel[t.tipo_transacao] || t.tipo_transacao}</TableCell>
                    <TableCell>
                      <Chip label={t.tipo_cartao} size="small"
                        color={t.tipo_cartao === 'VINTI4' ? 'success' : t.tipo_cartao === 'VISA' ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>{t.tipo_taxa}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {t.taxa_percentual > 0 ? `${t.taxa_percentual}%` : ''}{t.taxa_percentual > 0 && t.taxa_fixa > 0 ? ' + ' : ''}{t.taxa_fixa > 0 ? `${t.taxa_fixa} CVE` : ''}
                      {t.taxa_percentual === 0 && t.taxa_fixa === 0 ? '-' : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {t.taxa_sisp_percentual > 0 ? `${t.taxa_sisp_percentual}%` : ''}{t.taxa_sisp_percentual > 0 && t.taxa_sisp_fixa > 0 ? ' + ' : ''}{t.taxa_sisp_fixa > 0 ? `${t.taxa_sisp_fixa} CVE` : ''}
                      {t.taxa_sisp_percentual === 0 && t.taxa_sisp_fixa === 0 ? '-' : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {t.taxa_agente_percentual > 0 ? `${t.taxa_agente_percentual}%` : ''}{t.taxa_agente_percentual > 0 && t.taxa_agente_fixa > 0 ? ' + ' : ''}{t.taxa_agente_fixa > 0 ? `${t.taxa_agente_fixa} CVE` : ''}
                      {t.taxa_agente_percentual === 0 && t.taxa_agente_fixa === 0 ? '-' : ''}
                    </TableCell>
                    <TableCell>{t.vigencia_inicio?.slice(0, 10)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenTariff(t)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteTariff(t.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Tariff Dialog */}
      <Dialog open={tariffDialog} onClose={() => setTariffDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTariff ? 'Editar Tarifa' : 'Nova Tarifa'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 4 }}>
              <TextField fullWidth size="small" label="Codigo" value={tariffForm.codigo}
                onChange={(e) => setTariffForm({ ...tariffForm, codigo: e.target.value.toUpperCase() })}
                placeholder="Ex: G11" />
            </Grid>
            <Grid size={{ xs: 8 }}>
              <TextField fullWidth size="small" label="Nome" value={tariffForm.nome}
                onChange={(e) => setTariffForm({ ...tariffForm, nome: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Descricao" value={tariffForm.descricao}
                onChange={(e) => setTariffForm({ ...tariffForm, descricao: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField fullWidth size="small" select label="Tipo Transacao" value={tariffForm.tipo_transacao}
                onChange={(e) => setTariffForm({ ...tariffForm, tipo_transacao: e.target.value })}>
                <MenuItem value="RECARGA">Recarga</MenuItem>
                <MenuItem value="PAGAMENTO_SERVICO">Pagamento Servico</MenuItem>
                <MenuItem value="POS_VIRTUAL">POS Virtual WEB</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField fullWidth size="small" select label="Tipo Cartao" value={tariffForm.tipo_cartao}
                onChange={(e) => setTariffForm({ ...tariffForm, tipo_cartao: e.target.value })}>
                <MenuItem value="VINTI4">Vinti4</MenuItem>
                <MenuItem value="VISA">Visa</MenuItem>
                <MenuItem value="MASTERCARD">Mastercard</MenuItem>
                <MenuItem value="INTERNACIONAL">Internacional</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField fullWidth size="small" select label="Tipo Taxa" value={tariffForm.tipo_taxa}
                onChange={(e) => setTariffForm({ ...tariffForm, tipo_taxa: e.target.value })}>
                <MenuItem value="FIXO">Fixo (CVE/transacao)</MenuItem>
                <MenuItem value="PERCENTAGEM">Percentagem (%)</MenuItem>
                <MenuItem value="MISTO">Misto (% + fixo)</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}><Typography variant="subtitle2" color="text.secondary">Taxa Total (cobrada a entidade)</Typography></Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="Percentagem (%)" type="number" value={tariffForm.taxa_percentual}
                onChange={(e) => setTariffForm({ ...tariffForm, taxa_percentual: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="Fixo (CVE)" type="number" value={tariffForm.taxa_fixa}
                onChange={(e) => setTariffForm({ ...tariffForm, taxa_fixa: parseFloat(e.target.value) || 0 })} />
            </Grid>

            <Grid size={{ xs: 12 }}><Typography variant="subtitle2" color="info.main">Parte SISP</Typography></Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="SISP Percentagem (%)" type="number" value={tariffForm.taxa_sisp_percentual}
                onChange={(e) => setTariffForm({ ...tariffForm, taxa_sisp_percentual: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="SISP Fixo (CVE)" type="number" value={tariffForm.taxa_sisp_fixa}
                onChange={(e) => setTariffForm({ ...tariffForm, taxa_sisp_fixa: parseFloat(e.target.value) || 0 })} />
            </Grid>

            <Grid size={{ xs: 12 }}><Typography variant="subtitle2" color="success.main">Parte Agente (Pagali)</Typography></Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="Agente Percentagem (%)" type="number" value={tariffForm.taxa_agente_percentual}
                onChange={(e) => setTariffForm({ ...tariffForm, taxa_agente_percentual: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="Agente Fixo (CVE)" type="number" value={tariffForm.taxa_agente_fixa}
                onChange={(e) => setTariffForm({ ...tariffForm, taxa_agente_fixa: parseFloat(e.target.value) || 0 })} />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="Vigencia Inicio" type="date" value={tariffForm.vigencia_inicio}
                onChange={(e) => setTariffForm({ ...tariffForm, vigencia_inicio: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth size="small" label="Vigencia Fim (opcional)" type="date" value={tariffForm.vigencia_fim}
                onChange={(e) => setTariffForm({ ...tariffForm, vigencia_fim: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTariffDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveTariff} disabled={!tariffForm.codigo || !tariffForm.nome}>
            {selectedTariff ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SispBilling;
